/**
 * Operator Earnings Page
 *
 * Shows earnings calculation for operators based on verified domains
 */

import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Select,
  Space,
  Tag,
  message,
  Divider,
  Typography,
  Button,
  Alert,
  Segmented,
} from 'antd'
import {
  DollarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  LinkOutlined,
  GlobalOutlined,
  FileOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import 'dayjs/locale/ru'
import api from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

dayjs.locale('ru')

const { RangePicker } = DatePicker
const { Text, Title } = Typography

// Russian month names in genitive case
const monthNames: Record<number, string> = {
  1: 'января', 2: 'февраля', 3: 'марта', 4: 'апреля',
  5: 'мая', 6: 'июня', 7: 'июля', 8: 'августа',
  9: 'сентября', 10: 'октября', 11: 'ноября', 12: 'декабря'
}

interface PaymentPeriod {
  key: string
  year: number
  month: number
  half: 1 | 2
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  label: string
  shortLabel: string
}

// Generate payment periods for the last N months
function generatePaymentPeriods(monthsBack: number = 6): PaymentPeriod[] {
  const periods: PaymentPeriod[] = []
  const today = dayjs()

  for (let i = 0; i < monthsBack; i++) {
    const targetMonth = today.subtract(i, 'month')
    const year = targetMonth.year()
    const month = targetMonth.month() + 1
    const lastDay = targetMonth.daysInMonth()

    // Second half (16-lastDay)
    periods.push({
      key: `${year}-${month}-2`,
      year,
      month,
      half: 2,
      startDate: dayjs(`${year}-${month}-16`),
      endDate: dayjs(`${year}-${month}-${lastDay}`),
      label: `16-${lastDay} ${monthNames[month]} ${year}`,
      shortLabel: `16-${lastDay}.${String(month).padStart(2, '0')}`
    })

    // First half (1-15)
    periods.push({
      key: `${year}-${month}-1`,
      year,
      month,
      half: 1,
      startDate: dayjs(`${year}-${month}-01`),
      endDate: dayjs(`${year}-${month}-15`),
      label: `1-15 ${monthNames[month]} ${year}`,
      shortLabel: `1-15.${String(month).padStart(2, '0')}`
    })
  }

  return periods
}

// Get current payment period
function getCurrentPeriodKey(): string {
  const today = dayjs()
  const year = today.year()
  const month = today.month() + 1
  const half = today.date() <= 15 ? 1 : 2
  return `${year}-${month}-${half}`
}

interface EarningsPeriod {
  year: number
  month: number
  half: number
  period_start: string
  period_end: string
  label: string
}

interface OperatorEarnings {
  operator_id: number
  operator_username: string
  operator_full_name: string | null
  // Domains
  domain_correct_count: number
  domain_error_count: number
  // Pages
  page_correct_count: number
  page_error_count: number
  // Totals
  correct_count: number
  correct_amount: number
  error_count: number
  error_deduction: number
  total_earnings: number
  period_start?: string
  period_end?: string
}

interface EarningsResponse {
  period: EarningsPeriod
  operators?: OperatorEarnings[]
  operator_id?: number
  operator_username?: string
  operator_full_name?: string | null
  // Domains (for /my endpoint)
  domain_correct_count?: number
  domain_error_count?: number
  // Pages (for /my endpoint)
  page_correct_count?: number
  page_error_count?: number
  // Totals (for /my endpoint)
  correct_count: number
  correct_amount: number
  error_count: number
  error_deduction: number
  total_earnings?: number
  // Aggregates for /all endpoint
  grand_total_earnings?: number
  total_domain_correct?: number
  total_domain_errors?: number
  total_page_correct?: number
  total_page_errors?: number
  total_correct_count?: number
  total_correct_amount?: number
  total_error_count?: number
  total_error_deduction?: number
}

interface HistoryItem {
  id: number
  item_type: 'domain' | 'page'
  url: string
  status: string
  is_error: boolean
  amount: number
  processed_at: string
}

interface HistoryResponse {
  items: HistoryItem[]
  total_count: number
  period_start: string
  period_end: string
}

function EarningsPage() {
  const { operator } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [earnings, setEarnings] = useState<EarningsResponse | null>(null)
  const [history, setHistory] = useState<HistoryResponse | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)

  // Generate payment periods
  const paymentPeriods = useMemo(() => generatePaymentPeriods(12), [])
  const currentPeriodKey = useMemo(() => getCurrentPeriodKey(), [])

  // Get initial values from URL
  const initialMode = searchParams.get('mode') || 'periods'
  const initialPeriodKey = searchParams.get('period') || currentPeriodKey
  const initialPeriodStart = searchParams.get('period_start')
  const initialPeriodEnd = searchParams.get('period_end')

  const [mode, setMode] = useState<'periods' | 'custom'>(initialMode as 'periods' | 'custom')
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>(initialPeriodKey)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    initialPeriodStart && initialPeriodEnd
      ? [dayjs(initialPeriodStart), dayjs(initialPeriodEnd)]
      : null
  )

  const isManagerOrAdmin = operator?.role === 'manager' || operator?.role === 'admin'

  // Find selected period object
  const selectedPeriod = useMemo(() =>
    paymentPeriods.find(p => p.key === selectedPeriodKey),
    [paymentPeriods, selectedPeriodKey]
  )

  // Get current period index for navigation
  const currentPeriodIndex = useMemo(() =>
    paymentPeriods.findIndex(p => p.key === selectedPeriodKey),
    [paymentPeriods, selectedPeriodKey]
  )

  const fetchEarnings = async () => {
    try {
      setLoading(true)

      const params: Record<string, string> = {}

      if (mode === 'custom' && dateRange) {
        params.period_start = dateRange[0].format('YYYY-MM-DD')
        params.period_end = dateRange[1].format('YYYY-MM-DD')
      } else if (mode === 'periods' && selectedPeriod) {
        params.period_start = selectedPeriod.startDate.format('YYYY-MM-DD')
        params.period_end = selectedPeriod.endDate.format('YYYY-MM-DD')
      }

      const response = isManagerOrAdmin
        ? await api.getAllEarnings(params)
        : await api.getMyEarnings(params)

      setEarnings(response)
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Ошибка загрузки данных')
    } finally {
      setLoading(false)
    }
  }

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true)

      const params: Record<string, string> = {}

      if (mode === 'custom' && dateRange) {
        params.period_start = dateRange[0].format('YYYY-MM-DD')
        params.period_end = dateRange[1].format('YYYY-MM-DD')
      } else if (mode === 'periods' && selectedPeriod) {
        params.period_start = selectedPeriod.startDate.format('YYYY-MM-DD')
        params.period_end = selectedPeriod.endDate.format('YYYY-MM-DD')
      }

      const response = await api.getEarningsHistory(params)
      setHistory(response)
    } catch (error: any) {
      console.error('Failed to load history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    fetchEarnings()
    fetchHistory()
  }, [mode, selectedPeriodKey, dateRange])

  // Update URL when filters change
  useEffect(() => {
    const params: Record<string, string> = { mode }

    if (mode === 'periods') {
      params.period = selectedPeriodKey
    } else if (dateRange) {
      params.period_start = dateRange[0].format('YYYY-MM-DD')
      params.period_end = dateRange[1].format('YYYY-MM-DD')
    }

    setSearchParams(params)
  }, [mode, selectedPeriodKey, dateRange])

  // Navigate to previous period
  const goToPreviousPeriod = () => {
    if (currentPeriodIndex < paymentPeriods.length - 1) {
      setSelectedPeriodKey(paymentPeriods[currentPeriodIndex + 1].key)
    }
  }

  // Navigate to next period
  const goToNextPeriod = () => {
    if (currentPeriodIndex > 0) {
      setSelectedPeriodKey(paymentPeriods[currentPeriodIndex - 1].key)
    }
  }

  // Check if current period is selected
  const isCurrentPeriod = selectedPeriodKey === currentPeriodKey

  const columns: ColumnsType<OperatorEarnings> = [
    {
      title: 'Оператор',
      key: 'operator',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.operator_full_name || record.operator_username}</span>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            @{record.operator_username}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Проверено',
      dataIndex: 'correct_count',
      key: 'correct_count',
      align: 'center',
      render: (count: number) => (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          {count}
        </Tag>
      ),
    },
    {
      title: 'Начислено',
      dataIndex: 'correct_amount',
      key: 'correct_amount',
      align: 'right',
      render: (amount: number) => (
        <Text type="success">+{amount.toFixed(2)} руб.</Text>
      ),
    },
    {
      title: 'Ошибки',
      dataIndex: 'error_count',
      key: 'error_count',
      align: 'center',
      render: (count: number) => (
        <Tag color={count > 0 ? 'error' : 'default'} icon={count > 0 ? <WarningOutlined /> : undefined}>
          {count}
        </Tag>
      ),
    },
    {
      title: 'Вычтено',
      dataIndex: 'error_deduction',
      key: 'error_deduction',
      align: 'right',
      render: (amount: number) => (
        <Text type={amount > 0 ? 'danger' : 'secondary'}>
          {amount > 0 ? `-${amount.toFixed(2)} руб.` : '0.00 руб.'}
        </Text>
      ),
    },
    {
      title: 'Итого',
      dataIndex: 'total_earnings',
      key: 'total_earnings',
      align: 'right',
      render: (amount: number) => (
        <Text strong type={amount >= 0 ? 'success' : 'danger'}>
          {amount.toFixed(2)} руб.
        </Text>
      ),
    },
  ]

  // History table columns
  const historyColumns: ColumnsType<HistoryItem> = [
    {
      title: 'Тип',
      dataIndex: 'item_type',
      key: 'item_type',
      width: 80,
      align: 'center',
      render: (type: string) => (
        <Tag color={type === 'domain' ? 'blue' : 'purple'} icon={type === 'domain' ? <GlobalOutlined /> : <FileOutlined />}>
          {type === 'domain' ? 'Домен' : 'Страница'}
        </Tag>
      ),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string, record: HistoryItem) => (
        <a
          href={record.item_type === 'domain' ? `https://${url}` : url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <LinkOutlined />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</span>
        </a>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: string) => (
        <Tag color={status === 'active' || status === 'approved' ? 'success' : 'default'}>
          {status === 'active' ? 'Активен' : status === 'inactive' ? 'Неактивен' : status === 'approved' ? 'Одобрен' : 'Отклонён'}
        </Tag>
      ),
    },
    {
      title: 'Результат',
      dataIndex: 'is_error',
      key: 'is_error',
      width: 100,
      align: 'center',
      render: (isError: boolean) => (
        <Tag color={isError ? 'error' : 'success'} icon={isError ? <WarningOutlined /> : <CheckCircleOutlined />}>
          {isError ? 'Ошибка' : 'ОК'}
        </Tag>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (amount: number) => (
        <Text type={amount > 0 ? 'success' : 'danger'}>
          {amount > 0 ? '+' : ''}{amount.toFixed(2)} руб.
        </Text>
      ),
    },
    {
      title: 'Дата',
      dataIndex: 'processed_at',
      key: 'processed_at',
      width: 150,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
  ]

  // Get values for display
  const correctCount = isManagerOrAdmin ? earnings?.total_correct_count : earnings?.correct_count
  const correctAmount = isManagerOrAdmin ? earnings?.total_correct_amount : earnings?.correct_amount
  const errorCount = isManagerOrAdmin ? earnings?.total_error_count : earnings?.error_count
  const errorDeduction = isManagerOrAdmin ? earnings?.total_error_deduction : earnings?.error_deduction
  const totalEarnings = isManagerOrAdmin ? earnings?.grand_total_earnings : earnings?.total_earnings

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        Заработок
      </Title>

      {/* Period Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Mode selector */}
          <Space>
            <Segmented
              value={mode}
              onChange={(value) => setMode(value as 'periods' | 'custom')}
              options={[
                { label: 'Периоды выплат', value: 'periods' },
                { label: 'Произвольные даты', value: 'custom' },
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchEarnings}
              loading={loading}
            >
              Обновить
            </Button>
          </Space>

          {/* Period selector */}
          {mode === 'periods' && (
            <Space size="middle" wrap>
              {/* Navigation buttons */}
              <Button.Group>
                <Button
                  icon={<LeftOutlined />}
                  onClick={goToPreviousPeriod}
                  disabled={currentPeriodIndex >= paymentPeriods.length - 1}
                >
                  Пред.
                </Button>
                <Button
                  type={isCurrentPeriod ? 'primary' : 'default'}
                  onClick={() => setSelectedPeriodKey(currentPeriodKey)}
                >
                  Текущий
                </Button>
                <Button
                  onClick={goToNextPeriod}
                  disabled={currentPeriodIndex <= 0}
                >
                  След.
                  <RightOutlined />
                </Button>
              </Button.Group>

              {/* Period dropdown */}
              <Select
                value={selectedPeriodKey}
                onChange={setSelectedPeriodKey}
                style={{ minWidth: 220 }}
                popupMatchSelectWidth={false}
              >
                {paymentPeriods.map((period) => (
                  <Select.Option key={period.key} value={period.key}>
                    <Space>
                      {period.label}
                      {period.key === currentPeriodKey && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>текущий</Tag>
                      )}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Space>
          )}

          {/* Custom date range */}
          {mode === 'custom' && (
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]])
                } else {
                  setDateRange(null)
                }
              }}
              format="DD.MM.YYYY"
              style={{ width: 300 }}
            />
          )}
        </Space>
      </Card>

      {/* Selected period display */}
      {mode === 'periods' && selectedPeriod && (
        <Card
          style={{
            marginBottom: 24,
            background: isCurrentPeriod ? '#e6f7ff' : '#fafafa',
            borderColor: isCurrentPeriod ? '#91d5ff' : '#d9d9d9'
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="large">
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Период выплаты</Text>
                  <div style={{ fontSize: '18px', fontWeight: 500 }}>
                    {selectedPeriod.label}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Даты</Text>
                  <div>
                    {selectedPeriod.startDate.format('DD.MM.YYYY')} — {selectedPeriod.endDate.format('DD.MM.YYYY')}
                  </div>
                </div>
              </Space>
            </Col>
            <Col>
              {isCurrentPeriod && (
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  Текущий период
                </Tag>
              )}
            </Col>
          </Row>
        </Card>
      )}

      {mode === 'custom' && dateRange && (
        <Card style={{ marginBottom: 24, background: '#fafafa' }}>
          <Space size="large">
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>Выбранный период</Text>
              <div style={{ fontSize: '18px', fontWeight: 500 }}>
                {dateRange[0].format('DD.MM.YYYY')} — {dateRange[1].format('DD.MM.YYYY')}
              </div>
            </div>
          </Space>
        </Card>
      )}

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Корректно проверено"
              value={correctCount || 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Начислено"
              value={correctAmount || 0}
              precision={2}
              prefix="+"
              suffix="руб."
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Ошибки"
              value={errorCount || 0}
              prefix={<WarningOutlined style={{ color: errorCount ? '#ff4d4f' : '#d9d9d9' }} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Вычтено"
              value={errorDeduction || 0}
              precision={2}
              prefix="-"
              suffix="руб."
              valueStyle={{ color: errorDeduction ? '#ff4d4f' : '#d9d9d9' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Total */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="center">
          <Col>
            <Statistic
              title="Итого к выплате"
              value={totalEarnings || 0}
              precision={2}
              suffix="руб."
              valueStyle={{
                color: (totalEarnings || 0) >= 0 ? '#52c41a' : '#ff4d4f',
                fontSize: '32px'
              }}
            />
          </Col>
        </Row>
      </Card>

      <Divider />

      {/* Formula explanation */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Формула расчёта"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><Text strong>+4 руб.</Text> за каждый корректно проверенный домен или страницу</li>
            <li><Text strong type="danger">-10 руб.</Text> за каждую ошибку (изменено модератором)</li>
            <li><Text type="secondary">Итого = (Корректные x 4 руб.) - (Ошибки x 10 руб.)</Text></li>
          </ul>
        }
      />

      {/* Operators table (for managers) */}
      {isManagerOrAdmin && earnings?.operators && earnings.operators.length > 0 && (
        <Card title="Заработок по операторам">
          <Table
            dataSource={earnings.operators}
            columns={columns}
            rowKey="operator_id"
            loading={loading}
            pagination={false}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>ИТОГО</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <Text strong>{earnings.total_correct_count}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Text strong type="success">+{earnings.total_correct_amount?.toFixed(2)} руб.</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="center">
                    <Text strong>{earnings.total_error_count}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text strong type="danger">-{earnings.total_error_deduction?.toFixed(2)} руб.</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong type={(earnings.grand_total_earnings || 0) >= 0 ? 'success' : 'danger'}>
                      {earnings.grand_total_earnings?.toFixed(2)} руб.
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      )}

      {/* History table */}
      <Card title="История проверок" style={{ marginTop: 24 }}>
        <Table
          dataSource={history?.items || []}
          columns={historyColumns}
          rowKey="id"
          loading={historyLoading}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Всего: ${total}` }}
          size="small"
          locale={{ emptyText: 'Нет данных за выбранный период' }}
        />
      </Card>

      {/* No data message for regular operators */}
      {!isManagerOrAdmin && earnings && earnings.correct_count === 0 && earnings.error_count === 0 && (
        <Alert
          type="warning"
          showIcon
          message="Нет данных за выбранный период"
          description="В выбранном периоде нет обработанных доменов или страниц. Попробуйте выбрать другой период."
          style={{ marginTop: 24 }}
        />
      )}
    </div>
  )
}

export default EarningsPage
